/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import mbpLogger from '@module-logger/mbp';
import makeStyles from '@material-ui/core/styles/makeStyles';

import { getFeatureFlag } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { getIsBot } from '../../../../state/ducks/App/App-Selectors';
import useSalesforcePersonalizedContentQuery from '../../../helpers/Personalization/useSalesforcePersonalizedContentQuery';
import useClickStreamCustomEventsTracking from '../../../helpers/Personalization/useClickstreamCustomEventsTracking';
import { getCurrentPageType } from '../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Selectors';
import GenericSkeleton from '../GraphqlSkeletonComponents/GenericSkeleton';
import ConnectedSearch from './Search';
import SearchV2 from './SearchV2';
import SearchV3 from './SearchV3';
import { trackEvent } from '../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

const useStyles = makeStyles((theme) => ({
    searchSkeleton: {
        height: '38px',
        width: '100%',
        maxWidth: '727px',
        margin: '15px 0px 15px 40px',
        borderRadius: '3px',
        [theme.breakpoints.down(1026)]: {
            margin: '0 8px',
            width: 'auto',
            maxWidth: 'unset',
        },
    },
    mobileViewSkeleton: {
        paddingBottom: '8px',
    },
}));

function SearchBlock({
    typeSearchBox, brand, focusOnShow, hideHeaderOnScroll, searchButtonClick, isSearchUiInMobileMenuEnabled,
    mobileMenuCampaignResponse, isMobileHeaderHideOnScroll, persistedSearchValue, setPersistedSearchValue,
}) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const isSearchUiV3Enabled = useSelector(getFeatureFlag('is-search-ui-v3-enabled'));
    const isSearchUiV3ABTestingEnabled = useSelector(getFeatureFlag('is-search-ui-v3-enabled-ab-testing'));
    const isSearchBarBrandFilterEnabled = useSelector(getFeatureFlag('is-search-bar-brand-filter-enabled'));
    const isSearchBarBrandFilterABTestingEnabled = useSelector(getFeatureFlag('is-search-bar-brand-filter-ab-testing-enabled'));
    const isSearchUiTypeAheadEnabled = useSelector(getFeatureFlag('is-search-ui-type-ahead-enabled'));
    const isSearchUiTypeAheadABTestingEnabled = useSelector(getFeatureFlag('is-search-ui-type-ahead-ab-testing-enabled'));
    const isAutoSuggestExperimentsEnabled = useSelector(getFeatureFlag('is-auto-suggest-experiments-enabled')) || {};
    const isBot = useSelector(getIsBot);
    const pageType = useSelector(getCurrentPageType);
    if (isBot) {
        return (
            <SearchV3
                isDesktop={typeSearchBox === 'desktop'}
                brand={brand}
            />
        );
    }

    if (isSearchUiV3Enabled) {
        return (
            <SearchV3
                isDesktop={typeSearchBox === 'desktop'}
                brand={brand}
            />
        );
    }

    // if any one of the ab testing is enabled, then show skeleton at beginning
    const skeleton = (id) => {
        if (typeSearchBox === 'header') {
            return (
                <div className={classes.mobileViewSkeleton}>
                    <GenericSkeleton className={classes.searchSkeleton} id={id} />
                </div>
            );
        }
        return <GenericSkeleton className={classes.searchSkeleton} id={id} />;
    };

    if (isSearchUiV3ABTestingEnabled && typeof window === 'undefined') {
        return skeleton('searchBarV2Skeleton');
    }

    const {
        error: searchV3Error, data: searchV3Data, loading: searchV3Loading, variables: searchV3Variables,
    } = useSalesforcePersonalizedContentQuery({
        queryName: 'SearchBarUI',
        interactionName: `${brand.domain} - Get Campaign - Search Bar UI V3`,
        featureFlag: isSearchUiV3ABTestingEnabled,
        optInHoldoutParticipation: false,
        resolveIdentity: false,
        apolloOptions: {
            fetchPolicy: 'cache-first',
        },
    });

    useClickStreamCustomEventsTracking({
        salesforceResponse: searchV3Data?.contentSF,
        isFireImpression: typeSearchBox === 'desktop', // mobile - already tracked in MobileHeaderContainer
        page: { type: pageType },
    });

    // track search v3 ga4 experiment event
    useEffect(() => {
        if (
            typeof window !== 'undefined' && typeSearchBox === 'desktop'
            && !window.wuSearchUiV3CampaignEventFired && searchV3Data?.contentSF?.campaign?.campaignResponses?.[0]?.campaignName
        ) {
            trackEvent({
                eventCategory: 'Experiment',
                eventAction: searchV3Data?.contentSF?.campaign?.campaignResponses?.[0]?.campaignName,
                eventLabel: searchV3Data?.contentSF?.campaign?.campaignResponses?.[0]?.payload?.userGroup === 'Test' ? 'Variant' : 'Control',
                eventName: 'impression',
            });
            window.wuSearchUiV3CampaignEventFired = true;
        }
        return () => {
            window.wuSearchUiV3CampaignEventFired = false;
        };
    }, [searchV3Data?.contentSF, typeSearchBox]);

    if (searchV3Error) {
        mbpLogger.logError({
            function: 'SearchBlock',
            message: 'Error loading API data from searchBarUI',
            appName: process.env.npm_package_name,
            module: 'PersonalizedContent',
            jsError: searchV3Error,
        });
    }

    const searchV3UserGroup = searchV3Data?.contentSF?.campaign?.campaignResponses?.[0]?.payload?.userGroup;

    if (searchV3Loading || searchV3Variables?.skip) {
        return skeleton('searchBarV2LoadingSkeleton');
    }

    // track search v3 ga4 experiment event for desktop
    if (searchV3UserGroup && typeSearchBox === 'desktop') {
        dispatch(trackEvent({
            eventCategory: 'Experiment',
            eventAction: searchV3Data?.contentSF?.campaign?.campaignResponses?.[0]?.campaignName,
            eventLabel: searchV3UserGroup === 'Test' ? 'Variant' : 'Control',
            eventName: 'search',
        }));
    }

    if (searchV3UserGroup === 'Test') {
        return (
            <SearchV3
                isDesktop={typeSearchBox === 'desktop'}
                brand={brand}
                salesforceResponse={searchV3Data?.contentSF}
            />
        );
    }

    // if v2 is force enabled and type-ahead ab testing is not enabled - no problem just move with new version
    if (isSearchBarBrandFilterEnabled && !isSearchUiTypeAheadABTestingEnabled) {
        return (
            <SearchV2
                brand={brand}
                typeSearchBox={typeSearchBox}
                hideHeaderOnScroll={hideHeaderOnScroll}
                ffIsHideMobileHeaderOnScrollEnabled={isMobileHeaderHideOnScroll && !isSearchUiInMobileMenuEnabled}
                searchButtonClick={searchButtonClick}
                searchUiTypeAheadEnabled={isSearchUiTypeAheadEnabled}
                autoSuggestExperimentsEnabled={isAutoSuggestExperimentsEnabled}
                searchUiV3CampaignResponse={searchV3Data?.contentSF || {}}
            />
        );
    }

    // if v2 force and ab testing is not enabled - go to old version
    if (!isSearchBarBrandFilterEnabled && !isSearchBarBrandFilterABTestingEnabled) {
        return (
            <ConnectedSearch
                focusOnShow={focusOnShow}
                brand={brand}
                typeSearchBox={typeSearchBox}
                hideHeaderOnScroll={hideHeaderOnScroll}
                ffIsHideMobileHeaderOnScrollEnabled={isMobileHeaderHideOnScroll && !isSearchUiInMobileMenuEnabled}
                searchButtonClick={searchButtonClick}
                mobileMenuCampaignResponse={mobileMenuCampaignResponse}
                persistedSearchValue={persistedSearchValue}
                setPersistedSearchValue={setPersistedSearchValue}
                searchUiV3CampaignResponse={searchV3Data?.contentSF || {}}
            />
        );
    }

    if ((isSearchBarBrandFilterABTestingEnabled || isSearchUiTypeAheadABTestingEnabled) && typeof window === 'undefined') {
        return skeleton('searchBarV2Skeleton');
    }

    const {
        error, data, loading, variables,
    } = useSalesforcePersonalizedContentQuery({
        queryName: 'searchBarBrandFilter',
        interactionName: `${brand.domain} - Get Campaign - Search Bar Brand Filter`,
        featureFlag: isSearchBarBrandFilterABTestingEnabled,
        optInHoldoutParticipation: false,
        resolveIdentity: false,
        apolloOptions: {
            fetchPolicy: 'cache-first',
        },
    });

    const campaignTrackClickThrough = useClickStreamCustomEventsTracking({
        salesforceResponse: data?.contentSF,
        isFireImpression: true,
        page: { type: pageType },
    });

    const trackAllClickThrough = () => {
        campaignTrackClickThrough();
    };

    if (error) {
        mbpLogger.logError({
            function: 'SearchBlock',
            message: 'Error loading API data from searchBarBrandFilter',
            appName: process.env.npm_package_name,
            module: 'PersonalizedContent',
            jsError: error,
        });
    }

    const userGroup = data?.contentSF?.campaign?.campaignResponses?.[0]?.payload?.userGroup;

    const {
        error: typeAheadError, data: typeAheadData, loading: typeAheadLoading, variables: typeAheadVariables,
    } = useSalesforcePersonalizedContentQuery({
        queryName: 'searchUiTypeAhead',
        interactionName: `${brand.domain} - Get Campaign - Search ui type ahead`,
        featureFlag: isSearchUiTypeAheadABTestingEnabled,
        optInHoldoutParticipation: false,
        resolveIdentity: false,
        apolloOptions: {
            fetchPolicy: 'cache-first',
        },
    });

    useClickStreamCustomEventsTracking({
        salesforceResponse: typeAheadData?.contentSF,
        isFireImpression: true,
        page: { type: pageType },
    });

    if (loading || variables?.skip || typeAheadLoading || typeAheadVariables?.skip) {
        return skeleton('searchBarV2LoadingSkeleton');
    }

    if (typeAheadError) {
        mbpLogger.logError({
            function: 'SearchBlock',
            message: 'Error loading API data from searchUiTypeAhead',
            appName: process.env.npm_package_name,
            module: 'PersonalizedContent',
            jsError: typeAheadError,
        });
    }
    const typeAheadUserGroup = typeAheadData?.contentSF?.campaign?.campaignResponses?.[0]?.payload?.userGroup;

    // to make sure v2 is force enabled through FF as high priority than SF
    if (isSearchBarBrandFilterEnabled || userGroup === 'Test') {
        return (
            <SearchV2
                brand={brand}
                typeSearchBox={typeSearchBox}
                hideHeaderOnScroll={hideHeaderOnScroll}
                ffIsHideMobileHeaderOnScrollEnabled={isMobileHeaderHideOnScroll && !isSearchUiInMobileMenuEnabled}
                salesforceResponse={data?.contentSF}
                searchButtonClick={searchButtonClick}
                // to make sure type-ahead force enable as high priority
                searchUiTypeAheadEnabled={isSearchUiTypeAheadEnabled || typeAheadUserGroup === 'Test'}
                typeAheadSalesforceResponse={typeAheadData?.contentSF}
                mobileMenuCampaignResponse={mobileMenuCampaignResponse}
                autoSuggestExperimentsEnabled={isAutoSuggestExperimentsEnabled}
                searchUiV3CampaignResponse={searchV3Data?.contentSF || {}}
            />
        );
    }

    // if v2 is not force enabled and ab testing was not variant
    return (
        <ConnectedSearch
            focusOnShow={focusOnShow}
            brand={brand}
            typeSearchBox={typeSearchBox}
            trackClickThrough={trackAllClickThrough}
            hideHeaderOnScroll={hideHeaderOnScroll}
            ffIsHideMobileHeaderOnScrollEnabled={isMobileHeaderHideOnScroll && !isSearchUiInMobileMenuEnabled}
            searchButtonClick={searchButtonClick}
            mobileMenuCampaignResponse={mobileMenuCampaignResponse}
            persistedSearchValue={persistedSearchValue}
            setPersistedSearchValue={setPersistedSearchValue}
            searchUiV3CampaignResponse={searchV3Data?.contentSF || {}}
        />
    );
}

SearchBlock.propTypes = {
    typeSearchBox: PropTypes.string.isRequired,
    brand: PropTypes.object.isRequired,
    focusOnShow: PropTypes.bool,
    hideHeaderOnScroll: PropTypes.func,
    searchButtonClick: PropTypes.func,
    isSearchUiInMobileMenuEnabled: PropTypes.bool,
    mobileMenuCampaignResponse: PropTypes.object,
    isMobileHeaderHideOnScroll: PropTypes.bool,
    persistedSearchValue: PropTypes.string,
    setPersistedSearchValue: PropTypes.func,
};

SearchBlock.defaultProps = {
    focusOnShow: false,
    hideHeaderOnScroll: () => { },
    searchButtonClick: () => { },
    isSearchUiInMobileMenuEnabled: false,
    mobileMenuCampaignResponse: {},
    isMobileHeaderHideOnScroll: false,
    persistedSearchValue: '',
    setPersistedSearchValue: () => { },
};

export default SearchBlock;
